import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PROJECT_MODES,
  PROJECT_MODE_LABEL,
  SOURCE_TYPES,
  SOURCE_TYPE_LABEL,
  WINDOW_MAX_MONTHS,
  WINDOW_MIN_MONTHS,
  type ProjectMode,
  type SourceType,
} from "@/lib/projectSource";

/** O que o formulário guarda; a janela fica em texto para a caixa aceitar o meio da digitação. */
export interface ProjectSourceDraft {
  source_type: SourceType | "";
  mode: ProjectMode | "";
  window_months: string;
}

interface ProjectSourceFieldsProps {
  value: ProjectSourceDraft;
  onChange: (next: Partial<ProjectSourceDraft>) => void;
  idPrefix?: string;
}

/**
 * Origem, modo e janela do projeto (#1784), na criação e nas configurações.
 *
 * Origem e modo travam na primeira carga do ex-kit; a janela muda a qualquer hora. A trava é
 * da api (409 com o motivo): saber se há carga exige o banco do cliente, e o detalhe do projeto,
 * que toda tela lê, não deve depender dele.
 */
export function ProjectSourceFields({
  value,
  onChange,
  idPrefix = "project",
}: ProjectSourceFieldsProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-source-type`}>
          {t("Source system")} <span className="text-destructive">*</span>
        </Label>
        <Select
          value={value.source_type || undefined}
          onValueChange={(next) => onChange({ source_type: next as SourceType })}
        >
          <SelectTrigger id={`${idPrefix}-source-type`} aria-label={t("Source system")}>
            <SelectValue placeholder={t("Select the source system")} />
          </SelectTrigger>
          <SelectContent>
            {SOURCE_TYPES.map((source) => (
              <SelectItem key={source} value={source}>
                {t(SOURCE_TYPE_LABEL[source])}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-mode`}>
          {t("Mode")} <span className="text-destructive">*</span>
        </Label>
        <Select
          value={value.mode || undefined}
          onValueChange={(next) => onChange({ mode: next as ProjectMode })}
        >
          <SelectTrigger id={`${idPrefix}-mode`} aria-label={t("Mode")}>
            <SelectValue placeholder={t("Select the mode")} />
          </SelectTrigger>
          <SelectContent>
            {PROJECT_MODES.map((mode) => (
              <SelectItem key={mode} value={mode}>
                {t(PROJECT_MODE_LABEL[mode])}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground">
          {t("Source and mode lock once the project receives its first extraction kit load.")}
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-window-months`}>{t("Analysis window (months)")}</Label>
        <Input
          id={`${idPrefix}-window-months`}
          type="number"
          inputMode="numeric"
          min={WINDOW_MIN_MONTHS}
          max={WINDOW_MAX_MONTHS}
          value={value.window_months}
          onChange={(event) => onChange({ window_months: event.target.value })}
        />
        <p className="text-[11px] text-muted-foreground">
          {t("Months of transactional history the extraction and the Functional read (1 to 240).")}
        </p>
      </div>
    </div>
  );
}
